import React from "react"
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  Path,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import { RadioGroup, Radio } from "@headlessui/react"

import { QuestionRenderer } from "./question-renderer"
import { PlaceOrderFormData, Question } from "types"
import { getLocalizedText } from "utils/i18n/get-localized-text"

interface BranchingQuestionProps {
  question: Question
  register: UseFormRegister<PlaceOrderFormData>
  errors: FieldErrors<PlaceOrderFormData>
  watch: UseFormWatch<PlaceOrderFormData>
  setValue: UseFormSetValue<PlaceOrderFormData>
  ticketId: number
}

const BranchingQuestion: React.FC<BranchingQuestionProps> = ({
  question,
  register,
  errors,
  watch,
  setValue,
  ticketId,
}) => {
  const { t } = useTranslation()
  const sortedChoices = question.choices?.sort((a, b) => a.rank - b.rank) || []

  const questionLabel = getLocalizedText(question.text, "Question")

  const fieldName =
    `tickets.${ticketId}.${question.uuid}` as Path<PlaceOrderFormData>
  const fieldError = errors.tickets?.[ticketId]?.[question.uuid]

  const fieldValue = watch(fieldName)
  const selectedValues = Array.isArray(fieldValue) ? fieldValue : []
  const selectedChoiceId = selectedValues.length > 0 ? selectedValues[0] : null

  const clearBranchAnswers = (choiceId: number) => {
    const choice = question.choices?.find((c) => c.id === choiceId)
    if (choice?.question_branch) {
      choice.question_branch.forEach((branchQuestion) => {
        // Clear the answer for branch questions when parent selection changes
        // Use undefined to properly clear the field value
        setValue(
          `tickets.${ticketId}.${branchQuestion.uuid}` as Path<PlaceOrderFormData>,
          undefined as any,
          { shouldValidate: true }
        )
      })
    }
  }

  const getSelectedChoice = () => {
    if (!selectedChoiceId) return null
    return question.choices?.find((choice) => choice.id === selectedChoiceId)
  }

  const handleRadioChange = (value: number | null) => {
    const oldValue = selectedValues[0]

    if (value === null) {
      setValue(fieldName, [] as any, { shouldValidate: true })
    } else {
      setValue(fieldName, [value] as any, { shouldValidate: true })
    }

    if (oldValue && oldValue !== value) {
      clearBranchAnswers(oldValue)
    }
  }

  return (
    <div>
      <h4 className="block mb-3 text-sm font-medium text-theme_text">
        {questionLabel}
        {question.requires_answer && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </h4>

      {!sortedChoices.length ? (
        <div className="text-red-500">
          {t("validation.no_choices_available", "Error: No choices available for this question")}
        </div>
      ) : (
        <RadioGroup
          value={selectedChoiceId}
          onChange={handleRadioChange}
          className="space-y-2"
          name={fieldName}
        >
          <div className="space-y-3">
            {sortedChoices.map((choice) => (
              <Radio
                key={choice.id}
                value={choice.id}
                as="div"
                className={({ checked }) =>
                  `relative flex items-center cursor-pointer ${
                    checked ? "text-theme_text" : "text-theme_text"
                  }`
                }
              >
                {({ checked }) => (
                  <>
                    <div className="flex relative items-center">
                      <div
                        className={`relative shrink-0 w-[20px] h-[20px] rounded-full border-[2px] ${
                          checked
                            ? "border-theme_accent bg-theme_accent"
                            : "bg-white border-input_border"
                        }`}
                      >
                        {checked && (
                          <div className="absolute top-1/2 left-1/2 h-[10px] w-[10px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"></div>
                        )}
                      </div>
                    </div>
                    <span className="ml-3 text-sm text-theme_text">
                      {getLocalizedText(choice.text, "Choice")}
                    </span>
                  </>
                )}
              </Radio>
            ))}
          </div>
        </RadioGroup>
      )}

      <input
        type="hidden"
        {...register(fieldName, {
          required: question.requires_answer
            ? t("validation.option_required", "Please select an option")
            : false,
          validate: (value) => {
            const arr = Array.isArray(value) ? value : []
            if (!question.requires_answer && arr.length === 0) {
              return true
            }
            if (question.requires_answer && arr.length === 0) {
              return t("validation.option_required", "Please select an option")
            }
            return true
          },
        })}
      />

      {fieldError && (
        <p
          className="mt-2 text-sm text-red-500"
          role="alert"
          id={`${question.uuid}-error`}
        >
          {fieldError.message}
        </p>
      )}

      {(() => {
        const selectedChoice = getSelectedChoice()
        if (
          !selectedChoice ||
          !selectedChoice.question_branch ||
          selectedChoice.question_branch.length === 0
        ) {
          return null
        }

        const sortedBranchQuestions = selectedChoice.question_branch.sort(
          (a, b) => a.rank - b.rank
        )

        return (
          <div className="mt-6 pl-4 border-l-2 border-theme_accent/20">
            <div className="space-y-6">
              {sortedBranchQuestions.map((branchQuestion) => (
                <div key={branchQuestion.id}>
                  <QuestionRenderer
                    question={branchQuestion}
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    ticketId={ticketId}
                    isFirstTicket={false}
                    isSameAsContact={false}
                  />
                </div>
              ))}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

export { BranchingQuestion }
